import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Cropper from 'react-easy-crop';
import { CropAreaPixels, cropSquareToWebp, readFileAsDataURL } from '../utils/imageCompress';

interface MemberProfileImageUploadModalProps {
  isOpen: boolean;
  memberName: string;
  onClose: () => void;
  onSave: (blob: Blob) => Promise<void>;
}

const MemberProfileImageUploadModal: React.FC<MemberProfileImageUploadModalProps> = ({
  isOpen,
  memberName,
  onClose,
  onSave
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const cropPixelsRef = useRef<CropAreaPixels | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [rawImageSrc, setRawImageSrc] = useState('');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setIsDragging(false);
      setRawImageSrc('');
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setIsSaving(false);
      setError('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const handleFile = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('이미지 파일만 업로드할 수 있습니다.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('이미지 파일은 10MB 이하만 업로드할 수 있습니다.');
      return;
    }
    setError('');
    const dataUrl = await readFileAsDataURL(file);
    setRawImageSrc(dataUrl);
  }, []);

  const handleDrop: React.DragEventHandler<HTMLDivElement> = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    await handleFile(file);
  };

  const handleSaveClick = async () => {
    if (!rawImageSrc || !cropPixelsRef.current || isSaving) return;
    setIsSaving(true);
    setError('');
    try {
      const blob = await cropSquareToWebp(rawImageSrc, cropPixelsRef.current, 256, 0.8);
      await onSave(blob);
      onClose();
    } catch (e) {
      console.error(e);
      setError('이미지 저장에 실패했습니다. 다시 시도해 주세요.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>{memberName} 프로필 사진 설정</Title>
          <CloseButton onClick={onClose}>×</CloseButton>
        </Header>

        {!rawImageSrc ? (
          <DropZone
            $isDragging={isDragging}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setIsDragging(false);
            }}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            이미지를 드래그해서 놓거나 클릭해 업로드하세요
          </DropZone>
        ) : (
          <>
            <CropWrap>
              <Cropper
                image={rawImageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="rect"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(_, croppedAreaPixels) => {
                  cropPixelsRef.current = croppedAreaPixels as CropAreaPixels;
                }}
              />
            </CropWrap>
            <ZoomWrap>
              <span>확대</span>
              <input
                type="range"
                min={1}
                max={3}
                step={0.01}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
              />
            </ZoomWrap>
          </>
        )}

        {error && <ErrorText>{error}</ErrorText>}

        <Footer>
          <Button data-variant="modal-secondary" onClick={onClose} disabled={isSaving}>
            취소
          </Button>
          {rawImageSrc && (
            <Button $primary onClick={handleSaveClick} disabled={isSaving}>
              {isSaving ? '저장 중...' : '저장'}
            </Button>
          )}
        </Footer>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={(e) => {
            const input = e.currentTarget;
            const file = input.files?.[0];
            // 같은 파일 재선택이 가능하도록 즉시 초기화
            input.value = '';
            if (file) {
              void handleFile(file);
            }
          }}
        />
      </Modal>
    </Overlay>
  );
};

export default MemberProfileImageUploadModal;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 30020;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const Modal = styled.div`
  width: 100%;
  max-width: 520px;
  border-radius: 14px;
  background: var(--color-surface);
  border: var(--modal-border);
  box-shadow: var(--modal-shadow-large);
  padding: 16px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const Title = styled.h3`
  margin: 0;
  color: var(--color-text-primary);
  font-size: 18px;
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  color: var(--color-text-muted);
  font-size: 24px;
  cursor: pointer;
`;

const DropZone = styled.div<{ $isDragging: boolean }>`
  min-height: 220px;
  border: 2px dashed ${p => (p.$isDragging ? 'var(--color-accent-strong)' : 'var(--color-border)')};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-muted);
  background: var(--color-panel);
  cursor: pointer;
  text-align: center;
  padding: 16px;
`;

const CropWrap = styled.div`
  position: relative;
  width: 100%;
  height: 280px;
  border-radius: 12px;
  overflow: hidden;
  background: #111;
`;

const ZoomWrap = styled.div`
  margin-top: 12px;
  display: flex;
  gap: 12px;
  align-items: center;
  color: var(--color-text-primary);

  input {
    flex: 1;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 14px;
`;

const Button = styled.button<{ $primary?: boolean }>`
  border-radius: 8px;
  padding: 10px 14px;
  border: ${p => (p.$primary ? 'none' : '1px solid var(--color-border)')};
  background: ${p => (p.$primary ? 'var(--color-accent-strong)' : 'var(--color-surface)')};
  color: ${p => (p.$primary ? '#fff' : 'var(--color-text-primary)')};
  cursor: pointer;
`;

const ErrorText = styled.div`
  margin-top: 10px;
  color: var(--color-accent-strong);
  font-size: 13px;
`;
